import React from 'react';
import { MdCheckCircle } from 'react-icons/md';

interface PayLaterSuccessPopupProps {
  isOpen: boolean;
  onClose?: () => void;
  onContinue: () => void;
  orderNumber?: number;
  scheduledFor?: string;
  fulfillmentType?: string;
}

export const PayLaterSuccessPopup: React.FC<PayLaterSuccessPopupProps> = ({
  isOpen,
  onClose,
  onContinue,
  orderNumber,
  scheduledFor,
  fulfillmentType,
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white/90 rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all ring-1 ring-white/20">
        <div className="p-6 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4 shadow-md">
            <MdCheckCircle className="h-8 w-8 text-green-600" aria-hidden="true" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Order Confirmed
          </h3>

          {/* Message */}
          <p className="text-gray-600 mb-2">
            Thank you!
          </p>
          <p className="text-gray-600 mb-4">
            Your order has been placed successfully and is being prepared.
          </p>

          {/* Order Number */}
          {orderNumber && (
            <p className="text-sm text-gray-600 mb-6">
              Order ID: #ORD-{orderNumber}
            </p>
          )}

          {/* Scheduled Info */}
          {scheduledFor && (
            <div className="mb-6">
              {/* Separator Line */}
              <div className="border-t border-gray-300 mb-4"></div>
              
              {/* Header */}
              <div className="flex items-center justify-center mb-3">
                <span className="text-lg mr-2">🛍️</span>
                <p className="text-base font-semibold text-gray-900">
                  {fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'} Schedule
                </p>
              </div>
              
              {/* Date */}
              <div className="flex items-center justify-center mb-2">
                <span className="text-base mr-2">📅</span>
                <p className="text-gray-800">
                  {formatDate(scheduledFor)}
                </p>
              </div>
              
              {/* Time */}
              <div className="flex items-center justify-center mb-4">
                <span className="text-base mr-2">🕦</span>
                <p className="text-gray-800">
                  {formatTime(scheduledFor)}
                </p>
              </div>

              {/* Pickup Instruction */}
              {fulfillmentType === 'pickup' && (
                <p className="text-sm text-gray-600 mt-3">
                  Please arrive at your selected pickup location a few minutes before your scheduled time.
                </p>
              )}
              
              {/* Separator Line */}
              <div className="border-t border-gray-300 mt-4"></div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={onContinue}
              className="text-white py-3 px-6 rounded-lg font-semibold transition-colors focus:outline-none"
              style={{ backgroundColor: '#EF4421' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#D13A1C'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4421'}
            >
              Continue Shopping
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors focus:outline-none"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
